import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  GraduationCap,
  Hash,
  Bot,
  ShieldCheck,
  TrendingUp,
  Target,
  AlertTriangle,
  Check,
  X,
  Lightbulb,
  ChevronLeft,
  ChevronRight,
  Wallet,
  ArrowRight,
} from "lucide-react";
import { DERIV_AFFILIATE_URL } from "@/lib/socialLinks";

// v8.9.146 — Tutorial rewritten to match the CURRENT app. Removed all
// references to features that no longer exist: the Cloud Bot, the old
// Multiplier / NoTouch / Accumulator / BRR / Rise-Fall strategy ladder,
// "ALL (parallel)" mode, the manual Rise/Fall panel, auto-switch-to-hottest-
// symbol, and the WEAK / OPPORTUNIST / Any tiers. The app today has exactly
// two automated engines (Auto-FX forex bot + the Live Strategy Heatmap digit
// auto-trader) plus a manual Matches/Differs panel, all gated by the three
// quality tiers APEX / STRONG / PROVEN+.
const SECTIONS = [
  {
    id: "start",
    icon: GraduationCap,
    title: "Getting started",
    summary: "Login with Deriv · Demo vs Live · tier-based stake sizing",
    body: (
      <div className="space-y-4 text-sm leading-relaxed">
        <p className="text-vts-silver/85">
          VTS is a Deriv-connected trading desk. Everything runs through your own Deriv
          account — we never touch your funds. Start in <strong>Demo</strong> (practice $)
          and move to <strong>Live</strong> when you're ready.
        </p>
        <div className="space-y-2">
          <div className="flex items-start gap-2 text-vts-silver/80">
            <span className="text-vts-blue font-bold">1.</span>
            <span>
              Tap{" "}
              <span className="px-1.5 py-0.5 rounded bg-vts-blue/15 border border-vts-blue/40 text-vts-blue text-xs font-mono">
                Login with Deriv
              </span>{" "}
              in the header. Approve via Deriv's OAuth — no password ever leaves Deriv.
            </span>
          </div>
          <div className="flex items-start gap-2 text-vts-silver/80">
            <span className="text-vts-blue font-bold">2.</span>
            <span>
              Use the account dropdown to switch between your <strong>Demo</strong> and{" "}
              <strong>Real</strong> accounts. The yellow banner clears once you're on a
              real account.
            </span>
          </div>
          <div className="flex items-start gap-2 text-vts-silver/80">
            <span className="text-vts-blue font-bold">3.</span>
            <span>
              Tier auto-detects from your balance:&nbsp;
              <span className="font-mono text-[11px] text-white/90">
                Starter ($50+) · Explorer ($100+) · Builder ($300+) · Growth ($500+) · PRO ($1k+)
              </span>
              . Tier sets your base stake and daily limits.
            </span>
          </div>
        </div>
        <div className="rounded-md border border-vts-line bg-vts-surface px-3 py-2 text-[11px] text-vts-silver/60 font-mono">
          Tip: stay in Demo (or Dry-Run) until you've watched 30-50 trades and understand
          the numbers. Live ≠ Demo — real money, real psychology.
        </div>
      </div>
    ),
  },
  {
    id: "deposit",
    icon: Wallet,
    title: "Deposits & funding",
    summary: "Deposit on Deriv → TRANSFER to your Options (CR…) account",
    body: (
      <div className="space-y-4 text-sm leading-relaxed">
        <p className="text-vts-silver/85">
          Deriv splits your money across accounts. VTS and every bot trade from your{" "}
          <strong className="text-white">Options (CR…) account</strong> — money sitting
          anywhere else is <strong className="text-white">NOT used</strong>. Funding takes
          2 steps:
        </p>
        <div className="space-y-2">
          <div className="flex items-start gap-2 text-vts-silver/80">
            <span className="text-vts-blue font-bold">1.</span>
            <span>
              <strong className="text-white">Deposit on Deriv</strong> — open Deriv
              Cashier and deposit into your USD account (card / bank) or with crypto
              (BTC · ETH · USDT).
            </span>
          </div>
          <div className="flex items-start gap-2 text-vts-silver/80">
            <span className="text-vts-blue font-bold">2.</span>
            <span>
              <strong className="text-white">Transfer to your Options account</strong> —
              in Cashier → <strong className="text-white">Account transfer</strong>, move
              the money to the account starting with <strong className="text-white">CR…</strong>.
              That's the one VTS trades from.
            </span>
          </div>
          <div className="flex items-start gap-2 text-vts-silver/80">
            <span className="text-vts-blue font-bold">3.</span>
            <span>
              <strong className="text-white">Come back to VTS</strong> — your balance
              updates automatically. Start small; top up anytime.
            </span>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <a data-testid="tutorial-deposit-link" href={DERIV_AFFILIATE_URL} target="_blank" rel="noreferrer"
            className="flex items-center justify-center gap-1.5 rounded-lg bg-amber-400 text-black font-bold text-[12px] py-2.5 hover:brightness-110 transition">
            Deposit on Deriv <ArrowRight className="h-3.5 w-3.5" />
          </a>
          <a data-testid="tutorial-transfer-link" href="https://app.deriv.com/cashier/account-transfer" target="_blank" rel="noreferrer"
            className="flex items-center justify-center gap-1.5 rounded-lg border border-vts-blue/60 bg-vts-blue/15 text-white font-bold text-[12px] py-2.5 hover:bg-vts-blue/30 transition">
            Transfer funds <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
        <div className="rounded-md border border-amber-400/30 bg-amber-400/5 px-3 py-2 text-[11px] text-amber-200/90 font-mono">
          ⚠️ Most common mistake: depositing but skipping the transfer. If your VTS
          balance shows $0 after a deposit, the money is still in the wrong account —
          do step 2. The "Add funds" button in the header opens this guide anytime.
        </div>
      </div>
    ),
  },
  {
    id: "trade",
    icon: TrendingUp,
    title: "Trade tab",
    summary: "Manual Matches/Differs · the Live Strategy Heatmap auto-trader",
    body: (
      <div className="space-y-4 text-sm leading-relaxed">
        <p className="text-vts-silver/85">
          The <strong>Trade</strong> tab has one manual tool and one automated engine for
          Deriv's synthetic indices (Volatility, Jump, etc.).
        </p>
        <div className="space-y-3">
          <div className="rounded-lg border border-vts-line bg-vts-surface px-3 py-3">
            <div className="flex items-center gap-2 mb-1.5">
              <Hash className="h-3.5 w-3.5 text-vts-blue" />
              <span className="text-xs font-semibold text-white">Matches / Differs (manual)</span>
              <span className="text-[10px] text-vts-silver/60 font-mono">9× or 1.05× payout</span>
            </div>
            <p className="text-xs text-vts-silver/70 mb-1.5">
              Pick a digit 0-9. <strong>Matches</strong> wins if the last digit of the
              settling tick equals your pick (~10% chance, ~9× payout).{" "}
              <strong>Differs</strong> wins if it doesn't (~90% chance, ~1.05× payout).
            </p>
            <p className="text-[11px] text-vts-silver/60">
              The panel shows live digit frequency, Hot/Cold smart picks, and the actual
              payout multipliers straight from Deriv.
            </p>
          </div>
          <div className="rounded-lg border border-vts-profit/30 bg-vts-profit/5 px-3 py-3">
            <div className="flex items-center gap-2 mb-1.5">
              <Target className="h-3.5 w-3.5 text-vts-profit" />
              <span className="text-xs font-semibold text-white">Live Strategy Heatmap (auto-trader)</span>
            </div>
            <p className="text-xs text-vts-silver/70 mb-1.5">
              Paper-trades every digit strategy against live ticks 24/7 and shows which one
              is actually working <em>right now</em>. Turn on <strong>Auto-Trade</strong> and
              it fires the best strategy that clears your <strong>Min-edge</strong> tier
              (APEX / STRONG / PROVEN+ — see the Quality tiers section).
            </p>
            <p className="text-[11px] text-vts-silver/60">
              Use <strong>Dry-Run</strong> to watch it trade on paper first. The big colour
              banner tells you whether you're in paper or live mode.
            </p>
          </div>
        </div>
        <div className="rounded-md border border-vts-line bg-vts-surface px-3 py-2 text-[11px] text-vts-silver/60">
          The Live Trade Feed at the bottom of the tab shows every open and settled
          contract — manual or automated — with its floating P&L.
        </div>
      </div>
    ),
  },
  {
    id: "autofx",
    icon: Bot,
    title: "Bot tab — Auto-FX",
    summary: "The Forex binary bot · Dry-Run vs LIVE · how it picks trades",
    body: (
      <div className="space-y-4 text-sm leading-relaxed">
        <p className="text-vts-silver/85">
          The <strong>Bot</strong> tab runs <strong>Auto-FX</strong> — a bot that places
          Deriv binary contracts (CALL/PUT) on real Forex &amp; Gold pairs. It paper-trades
          a catalog of strategies 24/7 and only fires the ones that are{" "}
          <strong>currently winning</strong>.
        </p>
        <div className="space-y-2">
          <div className="font-semibold text-white text-[13px]">How it picks what to trade:</div>
          <div className="flex items-start gap-2 text-vts-silver/80">
            <span className="text-vts-blue font-bold">1.</span>
            <span>
              <strong>Quality filter</strong> — a strategy only qualifies with ≥50% win
              rate AND net-positive results AND a winning recent trend. Net-losers are
              blocked outright.
            </span>
          </div>
          <div className="flex items-start gap-2 text-vts-silver/80">
            <span className="text-vts-blue font-bold">2.</span>
            <span>
              <strong>Best available, top 1</strong> — among the strategies that have a
              live signal this minute, it fires the single highest-edge one. (It won't sit
              idle waiting for the all-time #1 if a different proven strategy is signalling
              now.)
            </span>
          </div>
          <div className="flex items-start gap-2 text-vts-silver/80">
            <span className="text-vts-blue font-bold">3.</span>
            <span>
              <strong>Pair whitelist</strong> — inside each strategy only its best pairs are
              allowed, re-checked every minute so cooling pairs drop within hours.
            </span>
          </div>
        </div>
        <div className="rounded-md border border-yellow-500/40 bg-yellow-500/10 px-3 py-2.5 text-[11px] text-yellow-100 leading-relaxed">
          <strong className="text-yellow-200">📋 Dry-Run vs 💸 LIVE — read this.</strong>{" "}
          Selecting a real account is <em>not</em> enough to trade real money. If the{" "}
          <strong>Dry-Run</strong> box is ticked the bot only <em>simulates</em> trades and
          your balance never moves. To trade for real: un-tick Dry-Run → tick the LIVE
          acknowledgement → Start. The Start button and the banner turn{" "}
          <span className="text-vts-profit font-semibold">green</span> in LIVE mode.
        </div>
        <div className="space-y-2">
          <div className="font-semibold text-white text-[13px]">Your controls:</div>
          <div className="text-[11px] text-vts-silver/75 space-y-1">
            <div><strong>Stake</strong> — per-trade amount ($0.35 – $1000).</div>
            <div><strong>Risk %</strong> — optional balance-based sizing.</div>
            <div><strong>Max trades / day</strong> — daily cap (up to 500).</div>
          </div>
        </div>
        <div className="space-y-2">
          <div className="font-semibold text-white text-[13px]">Reading the trade rows:</div>
          <div className="text-[11px] text-vts-silver/75 space-y-1.5">
            <div>
              <span className="font-mono text-vts-blue">⏱ 8:46 PM</span> = when this binary
              auto-resolves. No close button — Deriv binaries expire at a fixed time.
            </div>
            <div>
              <span className="font-mono text-yellow-300">📋 dry +$0.42</span> = Dry-Run with
              live floating P&amp;L. Will NOT touch your balance.
            </div>
            <div>
              <span className="font-mono text-vts-blue">⏳ open</span> = Live contract, still
              in-flight.
            </div>
            <div>
              <span className="font-mono text-vts-profit">✅ WIN +$0.95</span>{" "}
              / <span className="font-mono text-vts-loss">❌ LOSS -$1.00</span> = settled.
              ~0.95× win, -1× loss is the standard Deriv binary payout.
            </div>
          </div>
        </div>
        <div className="rounded-md border border-vts-blue/40 bg-vts-blue/10 px-3 py-2 text-[11px] text-vts-blue/90 leading-relaxed">
          <strong>The bot runs on the server.</strong> Closing this tab, locking your phone,
          or switching apps does NOT stop it — and it auto-resumes after server restarts.
          Come back later and check the History tab.
        </div>
      </div>
    ),
  },
  {
    id: "tiers",
    icon: Target,
    title: "Quality tiers",
    summary: "APEX 🐍 · STRONG · PROVEN+ — how the bots decide what's good enough",
    body: (
      <div className="space-y-3 text-sm leading-relaxed">
        <p className="text-vts-silver/85">
          Both auto-traders (Auto-FX and the Live Strategy Heatmap) gate every trade behind
          a <strong>Min-edge tier</strong>. Higher tier = fewer but higher-conviction
          trades. There are exactly three:
        </p>
        {[
          {
            n: "APEX 🐍",
            color: "vts-gold",
            tag: "★★ The cream only",
            desc: "STRONG by a 95% confidence interval AND a big live margin (≥8% above breakeven) AND ≥30 settled fires AND ≥$0.25 profit/trade. Fewest trades, highest conviction — best for protecting real funds.",
          },
          {
            n: "STRONG",
            color: "vts-profit",
            tag: "Math-honest",
            desc: "Only fires when the Wilson 95% CI lower bound is above breakeven. Better than PROVEN+, but still fires regularly.",
          },
          {
            n: "PROVEN+ 🎯",
            color: "vts-blue",
            tag: "★ Minimum / default",
            desc: "STRONG strategies plus ones whose live trend is clearly winning (≥4% margin above breakeven, ≥$0.15/trade, ≥15 fires). This is the loosest the bot will ever go — it never trades unproven picks.",
          },
        ].map((t) => (
          <div key={t.n} className="rounded-lg border border-vts-line bg-vts-surface px-3 py-2.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-xs font-semibold text-white">{t.n}</span>
              <span className={`text-[10px] uppercase tracking-wider font-mono text-${t.color}`}>
                {t.tag}
              </span>
            </div>
            <p className="text-[11px] text-vts-silver/75 leading-tight">{t.desc}</p>
          </div>
        ))}
        <div className="rounded-md border border-vts-line bg-vts-surface px-3 py-2 text-[11px] text-vts-silver/70">
          <strong className="text-white">Rule of thumb:</strong> trading real funds? Use{" "}
          <strong>APEX</strong> or <strong>STRONG</strong>. Want more frequent (still proven)
          trades while testing in Dry-Run? <strong>PROVEN+</strong>.
        </div>
      </div>
    ),
  },
  {
    id: "safety",
    icon: ShieldCheck,
    title: "Safety & honest expectations",
    summary: "What the bot can and cannot do · how not to blow up",
    body: (
      <div className="space-y-4 text-sm leading-relaxed">
        <div className="rounded-md border border-vts-loss/40 bg-vts-loss/10 px-3 py-3">
          <div className="flex items-center gap-2 mb-1.5">
            <AlertTriangle className="h-4 w-4 text-vts-loss" />
            <span className="font-semibold text-white text-sm">No bot beats Deriv consistently</span>
          </div>
          <p className="text-[11px] text-vts-silver/85">
            Every Deriv contract has a built-in house edge. Our quality filters and
            trend-gating tilt the odds slightly in your favour — they don't reverse the
            math. <strong>Expect winning days and losing days.</strong> Aim for a marginal
            positive edge over hundreds of trades, not overnight riches.
          </p>
        </div>
        <div className="space-y-2 text-xs">
          <div className="font-semibold text-white mb-1">Before you go LIVE:</div>
          {[
            "Check the account dropdown — Real, not Demo?",
            "Dry-Run UN-ticked and the banner is GREEN (LIVE)?",
            "Stake you can afford to lose on every single trade",
            "Min-edge set to APEX or STRONG for real funds",
            "A daily Max-trades cap you're comfortable with",
          ].map((step) => (
            <div key={step} className="flex items-start gap-2 text-vts-silver/80">
              <Check className="h-3.5 w-3.5 text-vts-profit mt-0.5 shrink-0" />
              <span>{step}</span>
            </div>
          ))}
        </div>
        <div className="space-y-2 text-xs">
          <div className="font-semibold text-white mb-1">When to stop:</div>
          {[
            "You've hit a loss you decided in advance — walk away for the day",
            "You're chasing losses → close the laptop",
            "Account dropdown showing the wrong account → STOP IMMEDIATELY",
          ].map((step) => (
            <div key={step} className="flex items-start gap-2 text-vts-silver/80">
              <X className="h-3.5 w-3.5 text-vts-loss mt-0.5 shrink-0" />
              <span>{step}</span>
            </div>
          ))}
        </div>
      </div>
    ),
  },
  {
    id: "faq",
    icon: Lightbulb,
    title: "FAQ",
    summary: "Why no SL/TP · why the bot isn't firing · paper vs live · etc.",
    body: (
      <div className="space-y-3 text-sm leading-relaxed">
        {[
          {
            q: "Where's the Stop Loss / Take Profit / close button?",
            a: "The bots place Deriv binary contracts (CALL/PUT). They auto-resolve at the duration badge (≈15 min). There's no manual close. Win = +$0.95 per $1 staked; Loss = -$1. The SL/TP shown in strategy details are signal-entry conditions, not draggable broker brackets.",
          },
          {
            q: "Why isn't the bot trading anything?",
            a: "Usually because very few strategies currently clear the quality bar AND have a live signal this minute — that's the bot being selective, not broken. Try lowering Min-edge to PROVEN+ for more (still proven) trades, give the 24/7 paper trader 5-10 min to gather fresh data, and confirm the bot is Started (the status badge shows running).",
          },
          {
            q: "APEX / STRONG / PROVEN+ — what's the difference?",
            a: "They're the three Min-edge quality tiers. APEX 🐍 = the cream only (STRONG + ≥8% margin + ≥30 fires + ≥$0.25/trade). STRONG = math-honest (95% CI lower bound above breakeven). PROVEN+ 🎯 = the default minimum (STRONG + clearly-trending strategies). See the Quality tiers section for details. (The old WEAK / OPPORTUNIST / Any tiers were removed.)",
          },
          {
            q: "Why isn't my real balance moving / why is it 'paper trading'?",
            a: "Dry-Run is ON. Selecting a real account is not enough — Dry-Run simulates every trade regardless of account. Stop the bot, UN-tick Dry-Run, tick the LIVE acknowledgement, then Start. The banner and Start button turn green in LIVE mode, and real wins/losses then hit your balance.",
          },
          {
            q: "Why does the trade history show no trades today?",
            a: "Usually timezone — the daily count rolls over at UTC midnight, so trades placed late local time can show under tomorrow. Or you switched accounts mid-session; switch back to the account that ran the trades.",
          },
          {
            q: "Why were trades stuck on 'open' forever?",
            a: "Fixed in v8.9.144. Background settle tasks could be garbage-collected on a server restart, leaving trades open. They now hold strong references and auto-recover on every runner start. If you ever see ancient open trades, click Stop then Start and they resolve within seconds.",
          },
          {
            q: "Why did it briefly say connecting / demo right after I logged in?",
            a: "The authenticated Deriv socket can take a few seconds to open after OAuth. You stay signed in the whole time — the connection self-heals on a fresh token (v8.9.146) instead of bouncing you out. If it persists, tap your account in the header to re-link Deriv.",
          },
        ].map((item) => (
          <details key={item.q} className="group rounded-md border border-vts-line/50 bg-vts-bg/30">
            <summary className="cursor-pointer list-none px-3 py-2 text-[12px] text-white font-medium flex items-start gap-2">
              <span className="text-vts-blue group-open:rotate-90 transition-transform">▶</span>
              <span className="flex-1">{item.q}</span>
            </summary>
            <div className="px-3 pb-3 pt-1 text-[11px] text-vts-silver/75 leading-relaxed">
              {item.a}
            </div>
          </details>
        ))}
      </div>
    ),
  },
];

const TutorialModal = ({ open, onOpenChange }) => {
  // Track which section is expanded — sidebar nav on desktop, scroll on
  // mobile. Default to first section ("Getting started").
  const [activeId, setActiveId] = useState(SECTIONS[0].id);
  const activeIdx = SECTIONS.findIndex((s) => s.id === activeId);
  const ActiveIcon = SECTIONS[activeIdx]?.icon || GraduationCap;

  const goNext = () => {
    const next = SECTIONS[(activeIdx + 1) % SECTIONS.length];
    setActiveId(next.id);
  };
  const goPrev = () => {
    const prev = SECTIONS[(activeIdx - 1 + SECTIONS.length) % SECTIONS.length];
    setActiveId(prev.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        data-testid="tutorial-modal"
        className="
          p-0 bg-vts-bg border-vts-line overflow-hidden
          w-screen h-[100dvh] max-w-none max-h-none rounded-none
          sm:w-auto sm:h-auto sm:max-w-3xl sm:max-h-[85vh] sm:rounded-lg
          flex flex-col
        "
      >
        <DialogHeader className="px-4 sm:px-5 pt-4 sm:pt-5 pb-3 border-b border-vts-line/50 shrink-0">
          <DialogTitle className="flex items-center gap-2 text-white text-base font-semibold">
            <GraduationCap className="h-5 w-5 text-vts-blue" />
            How to use VTS
          </DialogTitle>
          <p className="text-[11px] text-vts-silver/60 mt-0.5">
            Walkthrough · quality tiers · safety · honest expectations
          </p>
        </DialogHeader>
        <div className="flex flex-col lg:flex-row flex-1 min-h-0">
          {/* Sidebar — horizontal scroll on mobile, vertical on desktop. */}
          <nav
            data-testid="tutorial-sidebar"
            className="lg:w-56 shrink-0 border-b lg:border-b-0 lg:border-r border-vts-line/50 bg-vts-surface/30
                       overflow-x-auto lg:overflow-x-visible overflow-y-auto"
          >
            <ul className="flex lg:flex-col gap-1 p-2">
              {SECTIONS.map((s, i) => {
                const Icon = s.icon;
                const active = s.id === activeId;
                return (
                  <li key={s.id}>
                    <button
                      data-testid={`tutorial-nav-${s.id}`}
                      onClick={() => setActiveId(s.id)}
                      className={`w-full flex items-center gap-1.5 lg:gap-2 px-2.5 lg:px-3 py-1.5 lg:py-2 rounded-md text-left transition border whitespace-nowrap lg:whitespace-normal ${
                        active
                          ? "bg-vts-blue/15 border-vts-blue/40 text-white"
                          : "border-transparent text-vts-silver/70 hover:bg-vts-surface hover:text-white"
                      }`}
                    >
                      <Icon className={`h-3.5 w-3.5 lg:h-4 lg:w-4 shrink-0 ${active ? "text-vts-blue" : "text-vts-silver/50"}`} />
                      <div className="min-w-0">
                        <div className="text-[11px] lg:text-xs font-semibold leading-tight">
                          {i + 1}. {s.title}
                        </div>
                        <div className="hidden lg:block text-[10px] text-vts-silver/50 leading-tight mt-0.5">
                          {s.summary}
                        </div>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
          {/* Content pane */}
          <div className="flex-1 min-h-0 overflow-y-auto p-4 sm:p-5">
            <div className="flex items-center gap-2 mb-3">
              <ActiveIcon className="h-5 w-5 text-vts-blue" />
              <h3 className="text-base sm:text-lg font-semibold text-white">
                {SECTIONS[activeIdx]?.title}
              </h3>
            </div>
            <div
              data-testid={`tutorial-section-${activeId}`}
              className="text-[13px] sm:text-sm leading-relaxed break-words"
            >
              {SECTIONS[activeIdx]?.body}
            </div>
            <div className="mt-6 flex items-center justify-between gap-2 pt-4 border-t border-vts-line/50 sticky bottom-0 bg-vts-bg/95 backdrop-blur-sm -mx-4 sm:-mx-5 px-4 sm:px-5 pb-[calc(env(safe-area-inset-bottom,0px)+72px)] sm:pb-1">
              <Button
                data-testid="tutorial-prev"
                variant="outline"
                size="sm"
                onClick={goPrev}
                disabled={activeIdx === 0}
                className="border-vts-line bg-vts-surface text-vts-silver hover:bg-vts-surfaceHover h-9 px-3"
              >
                <ChevronLeft className="h-3.5 w-3.5 sm:mr-1" />
                <span className="hidden sm:inline">Previous</span>
              </Button>
              <span className="text-[10px] font-mono text-vts-silver/50">
                {activeIdx + 1} / {SECTIONS.length}
              </span>
              {activeIdx < SECTIONS.length - 1 ? (
                <Button
                  data-testid="tutorial-next"
                  size="sm"
                  onClick={goNext}
                  className="bg-vts-blue hover:bg-vts-blueHover text-white h-9 px-3"
                >
                  <span className="hidden sm:inline">Next</span>
                  <ChevronRight className="h-3.5 w-3.5 sm:ml-1" />
                </Button>
              ) : (
                <Button
                  data-testid="tutorial-done"
                  size="sm"
                  onClick={() => onOpenChange(false)}
                  className="bg-vts-profit hover:bg-vts-profit/90 text-black h-9 px-3"
                >
                  Got it
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TutorialModal;
